import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Container, Paper, Button, Slider, Tabs, Tab } from '@mui/material';
import NavBar from '../NavBar/navbar';
import FooterSection from '../Footer/footer';
import MathText from '../Math/MathText';
import { THEME, pageBackgroundSx, gridOverlaySx } from '../../theme';

// `variants` is an array of { key, label, equations: [string], description, simulate, parameters, canvasSize }
export default function BiologicalModelPage({ title, subtitle, description, variants }) {
    const [variantIdx, setVariantIdx] = useState(0);
    const canvasRef = useRef(null);
    const frameRef = useRef(null);
    const stateRef = useRef({});
    const [running, setRunning] = useState(false);

    const variant = variants[variantIdx];
    const [paramValues, setParamValues] = useState(
        (variant.parameters || []).reduce((acc, p) => ({ ...acc, [p.key]: p.default }), {})
    );

    useEffect(() => {
        setParamValues(
            (variant.parameters || []).reduce((acc, p) => ({ ...acc, [p.key]: p.default }), {})
        );
        setRunning(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variantIdx]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !variant.simulate) return undefined;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;
        stateRef.current = variant.simulate.init(width, height, paramValues);

        const draw = () => {
            variant.simulate.step(ctx, width, height, stateRef.current, paramValues);
            if (running) {
                frameRef.current = requestAnimationFrame(draw);
            }
        };

        draw();
        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
        };
    }, [running, paramValues, variant]);

    const handleReset = () => {
        const canvas = canvasRef.current;
        if (!canvas || !variant.simulate) return;
        stateRef.current = variant.simulate.init(canvas.width, canvas.height, paramValues);
        const ctx = canvas.getContext('2d');
        variant.simulate.step(ctx, canvas.width, canvas.height, stateRef.current, paramValues);
    };

    const canvasSize = variant.canvasSize || { width: 720, height: 360 };

    return (
        <Box sx={pageBackgroundSx}>
            <Box sx={gridOverlaySx} />
            <NavBar />
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: 12, pb: 8 }}>
                <Typography variant="overline" sx={{ color: THEME.primary, fontWeight: 'bold', letterSpacing: 2 }}>
                    EVOLVING SYSTEMS / BIOLOGICAL
                </Typography>
                <Typography variant="h3" fontWeight={800} color={THEME.textMain} sx={{ mb: 1 }}>
                    {title}
                </Typography>
                <Typography variant="h6" color={THEME.secondary} sx={{ mb: 3 }}>
                    {subtitle}
                </Typography>
                <Typography variant="body1" color={THEME.secondary} sx={{ mb: 3, lineHeight: 1.8 }}>
                    {description}
                </Typography>

                <Tabs
                    value={variantIdx}
                    onChange={(_, val) => setVariantIdx(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        mb: 3,
                        '& .MuiTab-root': { color: THEME.secondary, textTransform: 'none', fontWeight: 600 },
                        '& .Mui-selected': { color: THEME.primary },
                        '& .MuiTabs-indicator': { bgcolor: THEME.primary },
                    }}
                >
                    {variants.map((v) => (
                        <Tab key={v.key} label={v.label} />
                    ))}
                </Tabs>

                <Paper
                    sx={{
                        bgcolor: THEME.cardBg,
                        border: `1px solid ${THEME.border}`,
                        borderRadius: '12px',
                        p: 3,
                        mb: 3,
                    }}
                >
                    <Typography variant="subtitle2" color={THEME.primary} sx={{ mb: 1.5 }}>
                        Governing equations - {variant.label}
                    </Typography>
                    {variant.equations.map((eq, idx) => (
                        <Box key={idx} sx={{ mb: 1.5, overflowX: 'auto', color: THEME.textMain }}>
                            <MathText text={eq} display />
                        </Box>
                    ))}
                    {variant.description && (
                        <Typography variant="body2" color={THEME.secondary} sx={{ mt: 2, lineHeight: 1.7 }} component="div">
                            <MathText text={variant.description} />
                        </Typography>
                    )}
                </Paper>

                {variant.simulate && (
                    <Paper
                        sx={{
                            bgcolor: THEME.cardBg,
                            border: `1px solid ${THEME.border}`,
                            borderRadius: '12px',
                            p: 3,
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                onClick={() => setRunning((r) => !r)}
                                sx={{ bgcolor: THEME.primary, color: '#000', '&:hover': { bgcolor: THEME.primaryHover } }}
                            >
                                {running ? 'Pause' : 'Run Simulation'}
                            </Button>
                            <Button variant="outlined" onClick={handleReset} sx={{ borderColor: THEME.border, color: THEME.textMain }}>
                                Reset
                            </Button>
                        </Box>
                        {(variant.parameters || []).map((param) => (
                            <Box key={param.key} sx={{ mb: 2, maxWidth: 420 }}>
                                <Typography variant="body2" color={THEME.secondary}>
                                    {param.label}: {paramValues[param.key]}
                                </Typography>
                                <Slider
                                    value={paramValues[param.key] ?? param.default}
                                    min={param.min}
                                    max={param.max}
                                    step={param.step}
                                    onChange={(_, val) =>
                                        setParamValues((prev) => ({ ...prev, [param.key]: val }))
                                    }
                                    sx={{ color: THEME.primary }}
                                />
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <canvas
                                ref={canvasRef}
                                width={canvasSize.width}
                                height={canvasSize.height}
                                style={{
                                    width: '100%',
                                    maxWidth: canvasSize.width,
                                    border: `1px solid ${THEME.border}`,
                                    borderRadius: 8,
                                    background: '#000',
                                }}
                            />
                        </Box>
                    </Paper>
                )}
            </Container>
            <FooterSection />
        </Box>
    );
}
